/**
 * ShieldWall — Browser Fingerprint & Cross-Signal Consistency Engine
 *
 * Collects canvas, WebGL, AudioContext, and DOM signals to detect
 * headless browsers, Puppeteer Stealth, Playwright, and anti-detect
 * browsers (Multilogin, GoLogin, Dolphin Anty).
 *
 * The consistency engine correlates signals across layers to catch
 * spoofing that passes individual checks but fails cross-validation.
 *
 * References:
 *   - Research §3.1: Reliable headless detection signals
 *   - Research §3.2: Anti-detect browser evasions
 *   - Research §3.3: Headless detection that still works
 *   - DataDome Picasso pattern (canvas-based device fingerprinting)
 *   - CreepJS fingerprinting test suite
 *
 * @module fingerprint
 */

import type { BehaviorStats } from "../hooks/useBehaviorCollector";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface BrowserSignals {
  // Canvas
  canvasHash: number;
  canvasDataPrefix: string;
  /** True if two identical canvas renders produce the same output (anti-detect noise detection). */
  canvasStable: boolean;

  // WebGL
  webglVendor: string;
  webglRenderer: string;
  webglExtensions: number;

  // AudioContext
  audioSampleRate: number;
  audioMaxChannels: number;
  audioState: string;

  // Navigator
  webdriver: boolean;
  hasPlugins: boolean;
  pluginCount: number;
  hasLanguages: boolean;
  languageCount: number;
  cookiesEnabled: boolean;
  hardwareConcurrency: number;
  deviceMemory: number;
  maxTouchPoints: number;
  platform: string;

  // Screen
  screenRes: string;
  colorDepth: number;

  // Other
  timezone: string;
  doNotTrack: string | null;
  hasFetchAPI: boolean;
  permissionsAPI: boolean;
  /**
   * Permissions API notification state. Headless browsers often have this as
   * "denied" while Notification.permission is "default" — an impossible state.
   */
  permissionState: string;
}

export interface FingerprintResult {
  score: number;
  flags: string[];
}

export interface ConsistencyResult {
  flags: string[];
  penalty: number;
  coherenceScore: number;
}

// ─── Fingerprint Collector ───────────────────────────────────────────────────

/**
 * Collect browser fingerprint signals.
 * Privacy-respecting: we don't track users, just verify the client is a real browser.
 */
export function collectFingerprint(): BrowserSignals {
  const s: Partial<BrowserSignals> = {};

  // ── Canvas Fingerprint (render-based, not just hash) ──
  // We render twice and compare to detect anti-detect browsers that inject
  // random noise into the Canvas API (each render would differ).
  try {
    const renderCanvas = (): string => {
      const canvas = document.createElement("canvas");
      canvas.width = 280;
      canvas.height = 60;
      const ctx = canvas.getContext("2d")!;
      ctx.textBaseline = "alphabetic";
      ctx.fillStyle = "#e83";
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = "#069";
      ctx.font = "15px Arial";
      ctx.fillText("ShieldWall:\u2211\u222B\u03A9", 2, 17);
      ctx.fillStyle = "rgba(102,204,0,0.7)";
      ctx.font = "19px Georgia";
      ctx.fillText("\u03BC\u00B2\u2083\u2093", 4, 48);
      ctx.strokeStyle = "#f0f";
      ctx.arc(80, 30, 15, 0, Math.PI * 2);
      ctx.stroke();
      return canvas.toDataURL();
    };

    const render1 = renderCanvas();
    const render2 = renderCanvas();
    s.canvasHash = render1.length;
    s.canvasDataPrefix = render1.slice(0, 50);
    // Real browsers produce identical renders; anti-detect noise makes them differ
    s.canvasStable = render1 === render2;
  } catch (e) {
    console.error("Canvas fingerprint error:", e);
    s.canvasHash = 0;
    s.canvasDataPrefix = "";
    s.canvasStable = true; // Can't detect, default to stable
  }

  // ── WebGL (vendor, renderer, extensions count) ──
  try {
    const gl = document.createElement("canvas").getContext("webgl");
    if (gl) {
      const dbg = gl.getExtension("WEBGL_debug_renderer_info");
      s.webglVendor = dbg
        ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL)
        : "unknown";
      s.webglRenderer = dbg
        ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)
        : "unknown";
      s.webglExtensions = gl.getSupportedExtensions()?.length || 0;
    }
  } catch {
    s.webglVendor = "none";
    s.webglRenderer = "none";
    s.webglExtensions = 0;
  }

  // ── AudioContext Fingerprint ──
  try {
    const ac = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    s.audioSampleRate = ac.sampleRate;
    s.audioMaxChannels = ac.destination.maxChannelCount;
    s.audioState = ac.state;
    ac.close();
  } catch {
    s.audioSampleRate = 0;
    s.audioMaxChannels = 0;
    s.audioState = "failed";
  }

  // ── Navigator signals ──
  s.webdriver = !!navigator.webdriver;
  s.hasPlugins = (navigator.plugins?.length || 0) > 0;
  s.pluginCount = navigator.plugins?.length || 0;
  s.hasLanguages = (navigator.languages?.length || 0) > 0;
  s.languageCount = navigator.languages?.length || 0;
  s.cookiesEnabled = navigator.cookieEnabled;
  s.hardwareConcurrency = navigator.hardwareConcurrency || 0;
  s.deviceMemory = (navigator as any).deviceMemory || 0;
  s.maxTouchPoints = navigator.maxTouchPoints || 0;
  s.platform = navigator.platform;

  // ── Screen ──
  s.screenRes = `${screen.width}x${screen.height}`;
  s.colorDepth = screen.colorDepth;

  // ── Other ──
  s.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  s.doNotTrack = navigator.doNotTrack;
  s.hasFetchAPI = typeof fetch === "function";
  s.permissionsAPI = "Permissions" in window;

  // ── Permissions API state anomaly detection ──
  // Headless browsers often set Notification.permission to "denied" by default
  // while the Permissions API reports a different state, or vice versa.
  s.permissionState = "unknown";
  if ("Notification" in window) {
    s.permissionState = Notification.permission; // "default" | "granted" | "denied"
  }

  return s as BrowserSignals;
}

// ─── Fingerprint Scorer ──────────────────────────────────────────────────────

/**
 * Score browser fingerprint signals for bot likelihood.
 * Negative scores for suspicious signals, positive for real browser indicators.
 */
export function scoreBrowserFingerprint(
  signals: BrowserSignals
): FingerprintResult {
  let score = 0;
  const flags: string[] = [];

  // Critical: webdriver flag
  if (signals.webdriver) {
    score -= 30;
    flags.push("⛔ webdriver_active");
  }

  // Critical: SwiftShader = headless Chrome GPU
  if (signals.webglRenderer?.includes("SwiftShader")) {
    score -= 15;
    flags.push("⛔ swiftshader_headless");
  }

  // Positive signals
  if (signals.canvasHash > 1000) {
    score += 4;
    flags.push("✓ canvas_render");
  }

  if (
    signals.webglRenderer !== "none" &&
    signals.webglRenderer !== "unknown" &&
    !signals.webglRenderer?.includes("SwiftShader")
  ) {
    score += 4;
    flags.push("✓ real_gpu");
  }

  if (signals.webglExtensions > 10) {
    score += 2;
    flags.push("✓ webgl_extensions");
  }

  if (signals.audioSampleRate > 0) {
    score += 3;
    flags.push("✓ audio_context");
  }

  if (signals.hasPlugins) score += 2;
  if (signals.hasLanguages) score += 2;
  if (signals.cookiesEnabled) score += 1;

  if (
    signals.hardwareConcurrency > 0 &&
    signals.hardwareConcurrency <= 32
  ) {
    score += 2;
  }

  if (signals.permissionsAPI) score += 1;

  return { score: Math.max(score, 0), flags };
}

// ─── Cross-Signal Consistency Engine ─────────────────────────────────────────

/**
 * Correlate signals across layers to detect anti-detect browsers.
 *
 * Anti-detect tools (Multilogin, GoLogin, Dolphin Anty) can spoof
 * individual signals but fail cross-validation checks. Examples:
 *   - Mobile UA + desktop mouse precision + no touch
 *   - Chrome UA + SwiftShader GPU
 *   - Normal fingerprint + CDP automation artifacts
 *
 * This is the DataDome Picasso / Cloudflare approach from Research §3.2.
 */
export function analyzeConsistency(
  browserSignals: BrowserSignals,
  behaviorStats: BehaviorStats
): ConsistencyResult {
  const flags: string[] = [];
  let penalty = 0;

  const ua = navigator.userAgent.toLowerCase();
  const isMobileUA = /android|iphone|ipad|mobile/i.test(ua);
  const isDesktopMouse = behaviorStats.mousePoints > 20;
  const isTouchDevice = behaviorStats.touches > 0;

  // ── Mobile UA but extensive desktop mouse usage (no touch) ──
  if (isMobileUA && isDesktopMouse && !isTouchDevice) {
    flags.push("mobile_ua_desktop_mouse");
    penalty += 15;
  }

  // ── Desktop UA but only touch events (common for tablets — low penalty) ──
  if (!isMobileUA && isTouchDevice && behaviorStats.mousePoints < 3) {
    flags.push("desktop_ua_touch_only");
    penalty += 3;
  }

  // ── webdriver flag active ──
  if (browserSignals.webdriver) {
    flags.push("webdriver_active");
    penalty += 30;
  }

  // ── SwiftShader = headless Chrome GPU ──
  if (browserSignals.webglRenderer?.includes("SwiftShader")) {
    flags.push("swiftshader_gpu");
    penalty += 20;
  }

  // ── Canvas hash too low = stripped rendering pipeline ──
  if (browserSignals.canvasHash < 500 && browserSignals.canvasHash > 0) {
    flags.push("minimal_canvas_output");
    penalty += 10;
  }

  // ── No plugins + no languages = stripped browser environment ──
  if (!browserSignals.hasPlugins && !browserSignals.hasLanguages) {
    flags.push("stripped_browser_env");
    penalty += 15;
  }

  // ── Spoofed hardware concurrency ──
  if (
    browserSignals.hardwareConcurrency === 0 ||
    browserSignals.hardwareConcurrency > 64
  ) {
    flags.push("suspicious_hardware_concurrency");
    penalty += 8;
  }

  // ── Chrome DevTools Protocol artifacts ──
  // Puppeteer, Selenium, and other automation tools leave traces
  if (typeof window !== "undefined") {
    const hasCDP = Object.keys(window).some(
      (k) =>
        k.startsWith("cdc_") ||
        k.startsWith("__selenium") ||
        k.startsWith("__webdriver") ||
        k.startsWith("_Selenium") ||
        k.startsWith("calledSelenium")
    );
    if (hasCDP) {
      flags.push("cdp_artifacts_detected");
      penalty += 25;
    }
  }

  // ── AudioContext check (headless often has no audio output) ──
  if (browserSignals.audioMaxChannels === 0 && browserSignals.audioSampleRate > 0) {
    flags.push("no_audio_output");
    penalty += 10;
  }

  // ── Canvas Noise Detection (Anti-Detect Browser Defense) ──
  // Anti-detect browsers (Multilogin, GoLogin, Dolphin Anty) inject random
  // noise into the Canvas API so every session has a unique fingerprint.
  // Real browsers produce deterministic renders — two identical draws
  // always yield the same pixel output.
  if (browserSignals.canvasStable === false) {
    flags.push("canvas_noise_detected");
    penalty += 15;
  }

  // ── Permissions API Discrepancy ──
  // Real browsers: Notification.permission is "default" until the user acts.
  // Headless/automated: often pre-set to "denied" without user interaction,
  // while the Permissions API may report a conflicting state.
  if (
    browserSignals.permissionState === "denied" &&
    browserSignals.permissionsAPI &&
    !browserSignals.webdriver // avoid double-penalizing known webdriver
  ) {
    flags.push("permissions_anomaly");
    penalty += 10;
  }

  return {
    flags,
    penalty: Math.min(penalty, 60),
    coherenceScore: Math.max(0, 100 - Math.min(penalty, 60)),
  };
}

/** Same string as ShieldWallWidget uses for captcha device binding (server token <c>fph</c>). */
export function buildFingerprintHash(fp: BrowserSignals): string {
  return `${fp.canvasHash}-${fp.webglRenderer ?? "none"}-${fp.hardwareConcurrency}-${fp.platform ?? "none"}`;
}
